import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronLeft, ChevronRight, Clock, AlertTriangle, ArrowLeftRight, User, Users } from 'lucide-react';
import { startOfWeek, addWeeks, addDays, format, isSameDay, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { useCompanySchedule, type RealShift } from '@/hooks/useCompanySchedule';
import { formatTimeDisplay } from '@/types/shift-templates';

interface ScheduleViewProps {
  companyId: string;
}

export function ScheduleView({ companyId }: ScheduleViewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  const [weekOffset, setWeekOffset] = useState(0);
  const weekStart = useMemo(
    () => startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 }),
    [weekOffset]
  );

  const { patientSchedules, realShifts, isLoading } = useCompanySchedule(companyId, weekStart);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const getShiftsForPatientDay = (patientId: string, day: Date): RealShift[] => {
    return realShifts.filter(s =>
      s.patient_id === patientId && isSameDay(parseISO(s.check_in_at), day)
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const hasGaps = patientSchedules.some(p =>
    p.templates.some(t => t.caregivers.length === 0)
  );

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Section 1: Templates Overview */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground tracking-wide mb-3">
            {t('schedule.templatesTitle')}
          </h3>

          {patientSchedules.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{t('schedule.noPatients')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {patientSchedules.map(patient => (
                <Card key={patient.patient_id}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <SignedAvatar
                        src={patient.patient_photo_url}
                        bucket="patient-photos"
                        className="h-7 w-7"
                        fallback={
                          <span className="bg-primary/10 text-primary text-xs flex items-center justify-center w-full h-full">
                            {patient.patient_nickname.charAt(0).toUpperCase()}
                          </span>
                        }
                      />
                      <span className="font-medium text-sm">{patient.patient_nickname}</span>
                    </div>

                    {patient.templates.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">
                        {t('schedule.noTemplates')}
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {patient.templates.map(template => (
                          <div key={template.id} className="flex items-start gap-2 text-xs">
                            <Clock className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-medium">{template.name}</span>
                              <span className="text-muted-foreground ml-1">
                                ({formatTimeDisplay(template.default_start_time)}–{formatTimeDisplay(template.default_end_time)})
                              </span>
                              <span className="mx-1 text-muted-foreground">→</span>
                              {template.caregivers.length > 0 ? (
                                <span>
                                  {template.caregivers.map(c => c.full_name || t('common.unnamed')).join(', ')}
                                </span>
                              ) : (
                                <span className="text-destructive font-medium inline-flex items-center gap-0.5">
                                  <AlertTriangle className="h-3 w-3" />
                                  {t('schedule.noCoverage')}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Section 2: Weekly Real Shifts Grid */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-muted-foreground tracking-wide">
              {t('schedule.weeklyHistory')}
            </h3>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setWeekOffset(o => o - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs font-medium min-w-[140px] text-center">
                {format(weekStart, 'dd MMM', { locale })} – {format(addDays(weekStart, 6), 'dd MMM yyyy', { locale })}
              </span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setWeekOffset(o => o + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              {weekOffset !== 0 && (
                <Button variant="outline" size="sm" className="h-7 text-xs ml-1" onClick={() => setWeekOffset(0)}>
                  {t('schedule.today')}
                </Button>
              )}
            </div>
          </div>

          {patientSchedules.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <p>{t('schedule.noPatients')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-2 font-medium text-muted-foreground border-b border-border sticky left-0 bg-background z-10 min-w-[100px]">
                      {t('schedule.patient')}
                    </th>
                    {weekDays.map(day => (
                      <th
                        key={day.toISOString()}
                        className={`text-center p-2 font-medium border-b border-border min-w-[80px] ${
                          isSameDay(day, new Date()) ? 'text-primary bg-primary/5' : 'text-muted-foreground'
                        }`}
                      >
                        <div>{format(day, 'EEE', { locale })}</div>
                        <div className="text-[10px]">{format(day, 'dd/MM')}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {patientSchedules.map(patient => (
                    <tr key={patient.patient_id} className="border-b border-border/50">
                      <td className="p-2 font-medium sticky left-0 bg-background z-10">
                        <div className="flex items-center gap-1.5">
                          <SignedAvatar
                            src={patient.patient_photo_url}
                            bucket="patient-photos"
                            className="h-5 w-5"
                            fallback={
                              <span className="bg-primary/10 text-primary text-[10px] flex items-center justify-center w-full h-full">
                                {patient.patient_nickname.charAt(0).toUpperCase()}
                              </span>
                            }
                          />
                          <span className="truncate max-w-[80px]">{patient.patient_nickname}</span>
                        </div>
                      </td>
                      {weekDays.map(day => {
                        const shifts = getShiftsForPatientDay(patient.patient_id, day);
                        return (
                          <td key={day.toISOString()} className={`p-1 text-center align-top ${
                            isSameDay(day, new Date()) ? 'bg-primary/5' : ''
                          }`}>
                            {shifts.length === 0 ? (
                              <span className="text-muted-foreground/40">–</span>
                            ) : (
                              <div className="space-y-1">
                                {shifts.map(shift => (
                                  <ShiftCell key={shift.id} shift={shift} />
                                ))}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-primary/20 border border-primary/30" />
              {t('schedule.legendNormal')}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-amber-500/20 border border-amber-500/40" />
              <ArrowLeftRight className="h-2.5 w-2.5" />
              {t('schedule.legendSwap')}
            </span>
            <span className="flex items-center gap-1">
              <span>–</span>
              {t('schedule.legendNoShift')}
            </span>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

function ShiftCell({ shift }: { shift: RealShift }) {
  const { t } = useTranslation();
  const isSwap = shift.is_swap;
  const firstName = shift.caregiver_name?.split(' ')[0] ?? '?';
  const checkInTime = format(parseISO(shift.check_in_at), 'HH:mm');

  const tooltipContent = isSwap
    ? `${t('schedule.swapLabel')}: ${shift.caregiver_name ?? t('common.unnamed')} ${t('schedule.assumedShift')} ${shift.original_template_name ?? shift.shift_template_name ?? ''}`
    : `${shift.caregiver_name ?? t('common.unnamed')} • ${shift.shift_template_name ?? ''} • ${checkInTime}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={`rounded px-1 py-0.5 text-[10px] leading-tight truncate cursor-default ${
            isSwap
              ? 'bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-400'
              : 'bg-primary/10 border border-primary/20 text-foreground'
          }`}
        >
          <div className="flex items-center justify-center gap-0.5">
            {isSwap && <ArrowLeftRight className="h-2.5 w-2.5 shrink-0" />}
            <span className="truncate">{firstName}</span>
          </div>
          <div className="text-muted-foreground">{checkInTime}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[200px]">
        <p className="text-xs">{tooltipContent}</p>
      </TooltipContent>
    </Tooltip>
  );
}
